import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import {
  catchError,
  delay,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngxs/store';
import {
  AsyncPipe,
  DecimalPipe,
  NgForOf,
  UpperCasePipe,
} from '@angular/common';
import { TuiButtonModule, TuiDialogService } from '@taiga-ui/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@shared/services/alert.service';
import _ from 'lodash';
import { TuiActiveZoneModule, TuiLetModule } from '@taiga-ui/cdk';
import { SpaceNumberPipe } from '@shared/pipes/space-number.pipe';

import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import {
  RoutePreviewComponent,
  RoutePreviewInputData,
} from '@shared/components/google-map/route-preview/route-preview.component';
import { getLetterByIndex } from '@shared/utils/common';
import { getCargoRoutePoints } from '@shared/utils/cargo-route';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { CargoLoadingState } from '../../store/cargo-loading.state';
import {
  Cargo,
  CargoUnloadingAddress,
  CargoUnloadingAddressForm,
} from '../../../../types/cargo';
import { CargoLoadingService } from '../../services/cargo-loading.service';
import { CargoLoadingReplaceUnloadingAddressesJson } from '../../store/cargo-loading.actions';
import { getBoxAndPlaceQuantityInGroup } from '../../utils/box-place-quantity';

@Component({
  selector: 'app-unloading-addresses',
  standalone: true,
  imports: [
    AsyncPipe,
    TuiButtonModule,
    CdkDrag,
    CdkDropList,
    TuiLetModule,
    DecimalPipe,
    SpaceNumberPipe,
    UpperCasePipe,
    CheckboxComponent,
    ReactiveFormsModule,
    TuiActiveZoneModule,
    NgForOf,
  ],
  templateUrl: './unloading-addresses.component.html',
  styleUrl: './unloading-addresses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnloadingAddressesComponent implements OnInit {
  viewRoute$ = new Subject<void>();
  update$ = new Subject<void>();
  cargo$ = this.store.select(CargoLoadingState.getSelectedCargo);

  unloadingAddressesForm = this.fb.array<FormGroup<CargoUnloadingAddressForm>>(
    [],
  );

  updateSort$ = new Subject<
    Pick<CdkDragDrop<CargoUnloadingAddress>, 'currentIndex' | 'previousIndex'>
  >();

  boxAndPlaceQuantityPerClientAddress$ = this.store
    .select(CargoLoadingState.getOrders)
    .pipe(
      map(orders => {
        const groupByClientAddress = _.groupBy(orders, preOrder => {
          return this.getKey(
            preOrder.clientJson.name,
            preOrder.clientAddressWithCoordinatesJson.address,
          );
        });
        return getBoxAndPlaceQuantityInGroup(groupByClientAddress);
      }),
    );

  protected readonly getLetterByIndex = getLetterByIndex;

  offset$ = this.store.select(CargoLoadingState.getSelectedCargo).pipe(
    map(cargo => {
      return (
        (cargo?.loadingAddressesJson?.length ?? 0) +
        (cargo?.transitPointsJson?.length ?? 0)
      );
    }),
  );

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    protected readonly cargoLoadingService: CargoLoadingService,
    private readonly alert: AlertService,
    private readonly destroyRef: DestroyRef,
    private readonly dialogs: TuiDialogService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
  }

  onUpdateSort() {
    this.updateSort$
      .pipe(
        filter(params => params.currentIndex !== params.previousIndex),
        withLatestFrom(this.cargo$),
        switchMap(([{ previousIndex, currentIndex }, initialCargo]) => {
          if (!initialCargo) {
            return of(null);
          }
          const unloadingAddressesJson = structuredClone(
            initialCargo?.unloadingAddressesJson ?? [],
          );

          moveItemInArray(unloadingAddressesJson, previousIndex, currentIndex);
          this.replaceUnloadingAddresses(unloadingAddressesJson);
          const cargo: Cargo = {
            ...initialCargo,
            unloadingAddressesJson,
          };
          return this.cargoLoadingService.updateCargo(cargo).pipe(
            tap(() => {
              this.alert.showSuccess('Сортировка сохранена');
            }),
            catchError(() => {
              this.replaceUnloadingAddresses(
                initialCargo.unloadingAddressesJson ?? [],
              );
              this.alert.showError();
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onViewRoute() {
    this.viewRoute$
      .pipe(
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => {
          if (!cargo) {
            return throwError(() => new Error('empty cargo'));
          }
          const { allPoints, validPoints } = getCargoRoutePoints(cargo);
          if (allPoints.length !== validPoints.length) {
            this.alert.showWarning('Имеются записи без координат');
          }
          const data: RoutePreviewInputData = {
            routes: validPoints,
          };

          return this.dialogs.open(
            new PolymorpheusComponent(RoutePreviewComponent),
            {
              size: 'auto',
              data,
            },
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getKey(name: Nullable<string>, address: Nullable<string>) {
    return `${name}-${address}`;
  }

  replaceUnloadingAddresses(addresses: CargoUnloadingAddress[]) {
    this.store.dispatch(
      new CargoLoadingReplaceUnloadingAddressesJson(addresses),
    );
  }

  drop({ previousIndex, currentIndex }: CdkDragDrop<CargoUnloadingAddress[]>) {
    this.updateSort$.next({ previousIndex, currentIndex });
  }

  fillFormOnStateChange() {
    this.cargo$
      .pipe(
        tap(cargo => {
          const addresses = cargo?.unloadingAddressesJson ?? [];
          this.unloadingAddressesForm.clear({ emitEvent: false });
          addresses.forEach((address, index, items) => {
            const group = this.fb.group<CargoUnloadingAddressForm>({
              address: this.fb.control(address.address),
              approximateLocation: this.fb.control(address.approximateLocation),
              clientName: this.fb.control(address.clientName),
              latitude: this.fb.control(address.latitude),
              longitude: this.fb.control(address.longitude),
            });

            this.unloadingAddressesForm.push(group, {
              emitEvent: index === items.length - 1,
            });
          });
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  updateOnChanges() {
    this.update$
      .pipe(
        delay(0),
        filter(() => !this.unloadingAddressesForm.pristine),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => {
          const unloadingAddressesJson =
            this.unloadingAddressesForm.getRawValue();

          return this.cargoLoadingService.updateCargo({
            ...(cargo as Cargo),
            unloadingAddressesJson,
          });
        }),
        tap(() => {
          this.alert.showSuccess();
          this.unloadingAddressesForm.markAsUntouched();
          this.unloadingAddressesForm.markAsPristine();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.fillFormOnStateChange();
    this.updateOnChanges();
    this.onUpdateSort();
    this.onViewRoute();
  }
}
